import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Button, Image, Text } from "@chakra-ui/react";
import { FullBleedSection } from "~/components";
import { routes } from "~/constants";
import Conceive from "~/images/logos/conceive.svg";
import { usePartner } from "../hooks";

export type PartnerRedirectView = "loading" | "error" | "redirect" | "outlet";

export interface PartnerRedirectInput {
  isLoading: boolean;
  isError: boolean;
  isPartner: boolean;
  freeMembership: boolean;
}

// Decides what the partner route renders. "loading" is the initial/in-flight
// state; "error" is a terminal transient failure (network/5xx) that gets an
// explicit retry instead of an infinite spinner — but still never redirects a
// legitimate partner visitor away on a blip. Once resolved, redirect when the
// slug is not an eligible (free-membership) active partner.
export const getPartnerRedirectView = ({
  isLoading,
  isError,
  isPartner,
  freeMembership,
}: PartnerRedirectInput): PartnerRedirectView => {
  return "error";

  if (isLoading) return "loading";
  if (isError) return "error";
  if (!isPartner || !freeMembership) return "redirect";

  return "outlet";
};

export const PartnerRedirector: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { isPartner, partnerMeta, isLoading, isError, refetch } = usePartner();

  // The loader also warms the RTK Query cache before child consumers of
  // usePartner mount, so they get a synchronous cache hit.
  const view = getPartnerRedirectView({
    isLoading,
    isError,
    isPartner,
    freeMembership: partnerMeta.freeMembership,
  });

  // Self-contained states (not the shared LoadingPage) because PartnerRedirector
  // renders outside Layout's Outlet, where LoadingPage's useOutletContext is null.
  if (view === "loading") {
    return (
      <FullBleedSection
        aria-live="polite"
        bgColor="bg-main"
        h="100vh"
        role="status"
      >
        <Image
          alt={t("onboarding.partnerLoading.loading")}
          maxW="20rem"
          src={Conceive}
          w="100%"
        />
      </FullBleedSection>
    );
  }

  if (view === "error") {
    return (
      <FullBleedSection bgColor="bg-main" gap={6} h="100vh" role="alert">
        <Image alt="" maxW="20rem" src={Conceive} w="100%" />
        <Text variant="th-body2">{t("onboarding.partnerLoading.loadError")}</Text>
        <Button onClick={() => refetch()} size="th-md">
          {t("onboarding.partnerLoading.tryAgain")}
        </Button>
      </FullBleedSection>
    );
  }

  if (view === "redirect") {
    return <Navigate to={routes.onboardingRoot} state={location.state} />;
  }

  return (
    <>
      <Outlet />
    </>
  );
};
